import { HttpClient, HttpContext } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { AgencyType } from '@lib/agency/models';
import { UnitModel } from '@lib/incident/models';
import { OrganizationModel } from '@lib/organization/models';
import { EmployeeModel } from '@lib/person/models';
import { BACKGROUND_REQUEST, EnvironmentService, SKIP_PROBLEM_HANDLER } from '@lib/shared/data-access';
import { EMPTY, map, Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AgencyService {
  private httpClient = inject(HttpClient);
  private environmentService = inject(EnvironmentService);

  // signals
  private unitsSourceSignal = signal<UnitModel[]>([]);
  public unitsComputedSignal = computed(() => {
    return this.unitsSourceSignal();
  });

  private editUnitSourceSignal = signal<UnitModel | null>(null);
  public editUnitComputedSignal = computed(() => {
    return this.editUnitSourceSignal();
  });

  public getAgencyOfficers(
    organizationId: number
  ): Observable<EmployeeModel[]> {
    return this.httpClient.get<EmployeeModel[]>(
      this.environmentService
        .pathfinderApiUrl
        .concat(`/api/v1/agencies/${organizationId}/officers`)
    );
  }

  public getAgenciesByAgencyType(agencyType: AgencyType) {
    return this.httpClient.get<OrganizationModel[]>(
      this.environmentService
        .pathfinderApiUrl
        .concat(`/api/v1/agencies/${agencyType}`)
    );
  }

  public getAgencySummaries(isMasterAgenciesOnly?: boolean) {
    return this.httpClient.get<OrganizationModel[]>(
      this.environmentService
        .pathfinderApiUrl
        .concat(`/api/v1/agencies${isMasterAgenciesOnly ? '?isMasterAgenciesOnly=true': ''}`)
    );
  }

  /**
   * @param background a re-pull the app makes on its own (CAD after a hub reconnect), not the officer: a
   *   `BACKGROUND_REQUEST`, which does not count as user activity for the idle logout (the CJIS session lock).
   */
  public getUnits(background = false) {
    return this.httpClient
      .get<UnitModel[]>(this.environmentService.pathfinderApiUrl.concat(`/api/v1/units`), {
        // The app's own read is out of the Problem toast too: a refusal or a server fault on a timer must not
        // interrupt whatever screen the officer is on. The caller falls back to what the device holds.
        context: new HttpContext().set(BACKGROUND_REQUEST, background).set(SKIP_PROBLEM_HANDLER, background),
      })
      .pipe(
        map((units: UnitModel[]) =>
          units
            .map((unit) => ({
              ...unit,
              disabled: unit.isExpired ?? false
            }))
            .sort((a, b) => (a.isExpired === b.isExpired ? 0 : a.isExpired ? 1 : -1))
        ),
        tap((units) => this.unitsSourceSignal.set(units))
      );
  }

  public getUnitsByOrganizationId(organizationId: number) {
    if (organizationId > 0) {
      return this.httpClient
        .get<UnitModel[]>(this.environmentService.pathfinderApiUrl.concat(`/api/v1/units/${organizationId}`))
        .pipe(
          map((units: UnitModel[]) => 
            units
              .map((unit) => ({
                ...unit,
                disabled: unit.isExpired ?? false
              }))
              .sort((a, b) => (a.isExpired === b.isExpired ? 0 : a.isExpired ? 1 : -1))
          ),
          tap((units) => this.unitsSourceSignal.set(units))
        );
    } else {
      this.unitsSourceSignal.set([]);
      return EMPTY;
    }
  }

  public updateUnitById(unitId: number, unit: UnitModel) {
    return this.httpClient.put<UnitModel>(
      `${this.environmentService.pathfinderApiUrl}/api/v1/units/${unitId}`,
      unit
    ).pipe(
        tap((result) => {
            this.unitsSourceSignal.update(units => units.map(unit => unit.id === result.id ? result : unit));
        }),
    )
  }

  public createNewUnit(unit: UnitModel) {
    return this.httpClient.post<UnitModel>(
      this.environmentService
        .pathfinderApiUrl
        .concat(`/api/v1/units`),
      unit
    ).pipe(
        tap((response) => {
            this.unitsSourceSignal.update(units => ([...units, response]));
        })
    );
  }

  // signal set
  public setEditUnit(unit: UnitModel | null) {
    this.editUnitSourceSignal.set(unit);
  }
}